import dayjs from "dayjs";
import { Bookmark, Ellipsis } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
	BriefcaseIcon,
	CalendarIcon,
	DollarSign,
	MapPinIcon,
	UsersIcon,
} from "@/app/assets/icons/interface/interface.jsx";
import { Button } from "@/app/components/ui/button";
import { currencyFormat } from "@/app/handlers/utilityFunctions.js";
import { JobDetails } from "../utils/helpers";

interface JobMetadataProps {
	jobDetails: JobDetails;
	showPostGenerator: boolean;
	onMenuClick: () => void;
	onSave?: () => void;
	onShare?: () => void;
	onNotRelevant?: () => void;
	isExpired?: boolean;
}

export function JobMetadata({
	jobDetails,
	showPostGenerator,
	onMenuClick,
	onSave,
	onShare,
	onNotRelevant,
	isExpired,
}: JobMetadataProps) {
	const [showMoreMenu, setShowMoreMenu] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const handleSave = () => {
		if (!onSave) return;
		setIsSaving(true);
		onSave();
		setTimeout(() => setIsSaving(false), 1000);
	};

	const _handleShare = async () => {
		const shareUrl = `${window.location.origin}/jobs/${jobDetails?._id}`;
		const shareText = `Check out this opportunity for a ${jobDetails?.jobTitle} at ${jobDetails?.company?.company_name || ""}`;

		if (navigator.share) {
			try {
				await navigator.share({
					title: jobDetails?.jobTitle || "Job Opportunity",
					text: shareText,
					url: shareUrl,
				});
			} catch (err) {
				// User cancelled or share failed — no action needed
				if ((err as Error).name === "AbortError") return;
			}
		} else {
			await navigator.clipboard.writeText(shareUrl);
		}

		onShare?.();
	};

	return (
		<div className="flex justify-between">
			<div className="flex-1">
				<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
					<div className="flex items-center mb-4 sm:mb-0">
						<Link to={`/companies/${jobDetails?.company?._id}`} state={jobDetails?.company}>
							<img
								alt={`${jobDetails?.company?.company_name || ""} logo`}
								loading="lazy"
								width="64"
								height="64"
								decoding="async"
								data-nimg="1"
								className="rounded-lg mr-4"
								style={{ color: "transparent" }}
								src={jobDetails?.company ? jobDetails?.company?.logo_url : "/images/co-placeholder.png"}
								onError={(e) => ((e.target as HTMLImageElement).src = "/images/co-placeholder.png")}
							/>
						</Link>
						<div>
							<div className="flex items-center gap-2 flex-wrap">
								<h1 className="text-2xl font-bold text-gray-900 font-plus-jakarta">{jobDetails?.jobTitle}</h1>
								{isExpired && (
									<span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800 font-medium">Closed</span>
								)}
							</div>
							<Link to={`/companies/${jobDetails?.company?._id}`} state={jobDetails?.company}>
								<p className="text-lg text-gray-700 font-inter">{jobDetails?.company?.company_name || ""}</p>
							</Link>
						</div>
					</div>
				</div>

				{/* Job metadata grid */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
					<div className="flex items-center">
						<MapPinIcon className="w-5 h-5 mr-2 text-gray-500" />
						<span className="text-gray-700 text-sm">
							{jobDetails?.jobLocation?.city}, {jobDetails?.jobLocation?.region}
						</span>
					</div>
					<div className="flex items-center">
						<CalendarIcon className="w-5 h-5 mr-2 text-gray-500" />
						<span className="text-gray-700 text-sm">Deadline {dayjs(jobDetails?.closeAt).format("DD MMMM, YYYY")}</span>
					</div>
					<div className="flex items-center">
						<BriefcaseIcon className="w-5 h-5 mr-2 text-gray-500" />
						<span className="text-gray-700 text-sm">{jobDetails?.employmentType}</span>
					</div>
					<div className="flex items-center">
						<UsersIcon className="w-5 h-5 mr-2 text-gray-500" />
						<span className="text-gray-700 text-sm">{jobDetails?.company?.company_size_approx} Employees</span>
					</div>
					<div className="flex items-center">
						<DollarSign className="w-5 h-5 mr-2 text-gray-500" />
						<span className="text-gray-700 text-sm">
							{(jobDetails?.salary?.minPay ?? 0) > 1 ? (
								<>
									{jobDetails?.salary?.payCurrency}{" "}
									{jobDetails?.salary?.payOption === "Exact Amount" ? (
										<>
											{currencyFormat(jobDetails?.salary?.minPay)} {jobDetails?.salary?.payFrequency}
										</>
									) : (
										<>
											{currencyFormat(jobDetails?.salary?.minPay)} - {jobDetails?.salary?.payCurrency}{" "}
											{currencyFormat(jobDetails?.salary?.maxPay)} {jobDetails?.salary?.payFrequency}
										</>
									)}
								</>
							) : (
								"Negotiable"
							)}
						</span>
					</div>
				</div>
			</div>
			{/* Action buttons - Save, Share, Menu */}
			{!isExpired && (
				<div className="flex items-center gap-2 shrink-0">
					{/* Save Button */}
					<button
						disabled={isSaving}
						className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-3 h-9 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50"
						onClick={handleSave}
					>
						<Bookmark className="h-4 w-4" />
						<span className="hidden sm:inline">{isSaving ? "Saving..." : "Save"}</span>
					</button>

					{/* Menu Dropdown - Generate Image + Not Relevant */}
					<div className="relative">
						<Button size="sm" variant="outline" className="h-9 w-9 p-0" onClick={() => setShowMoreMenu(!showMoreMenu)}>
							<Ellipsis className="w-4 h-4" />
						</Button>

						{showMoreMenu && (
							<div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-[350px]">
								{showPostGenerator && (
									<button
										className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 text-nowrap"
										onClick={() => {
											onMenuClick();
											setShowMoreMenu(false);
										}}
									>
										Generate Image
									</button>
								)}
								{onNotRelevant && (
									<button
										className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
										onClick={() => {
											onNotRelevant();
											setShowMoreMenu(false);
										}}
									>
										Not Relevant
									</button>
								)}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
